import { useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import {
  CustomerPicker,
  EmptyRow,
  HIGHLIGHT_STYLES,
  HighlightLegend,
  LoadingOverlay,
  RECEIVABLES_API,
  fmtDate,
  money,
  useReceivableLookups,
  useSort,
} from "./common";
import { MultiSelect } from "./common";

const COLUMNS = [
  { key: "invoiceNumber", label: "Invoice #" },
  { key: "customerName", label: "Customer" },
  { key: "beatName", label: "Beat" },
  { key: "salesmanName", label: "Salesman" },
  { key: "invoiceDate", label: "Invoice Date", format: fmtDate },
  { key: "dueDate", label: "Due Date", format: fmtDate },
  { key: "invoiceAmount", label: "Invoice Amount", format: money, align: "right" },
  { key: "balanceAmount", label: "Balance Amount", format: money, align: "right" },
  { key: "ageingDays", label: "Overdue Days", align: "center" },
];

/** Every unpaid bill with the reason it is still open (legacy OutstandingInvoicesStatement.aspx). */
const OutstandingInvoicesStatement = () => {
  const { salesmen, beats } = useReceivableLookups(["salesmen", "beats"]);
  const [salesmanUserId, setSalesmanUserId] = useState("");
  // several beats of the salesman may be picked
  const [beatIds, setBeatIds] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const rows = statement?.rows || [];
  const { sorted, toggle, indicator } = useSort(rows);
  const beatOptions = salesmanUserId ? beats.filter((b) => String(b.salesmanUserId) === String(salesmanUserId)) : beats;

  const params = () => ({ salesmanUserId, beatIds: beatIds.length ? beatIds.join(",") : undefined, customerId: customer?.id });

  const load = async () => {
    setLoading(true);
    try {
      setStatement(await api.get(`${RECEIVABLES_API}/outstanding-invoices`, params()));
    } catch (error) {
      toast.error(error.message || "Failed to load outstanding invoices");
    } finally {
      setLoading(false);
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      await api.downloadFile(`${RECEIVABLES_API}/outstanding-invoices/pdf`, params(), "outstanding-invoices.pdf");
    } catch (error) {
      toast.error(error.message || "PDF failed");
    } finally {
      setExporting(false);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      await api.downloadFile(`${RECEIVABLES_API}/outstanding-invoices/export`, params(), "outstanding-invoices.xlsx");
    } catch (error) {
      toast.error(error.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} label="Loading outstanding invoices..." />
      <div className="filters-bar">
        <select
          value={salesmanUserId}
          onChange={(e) => {
            setSalesmanUserId(e.target.value);
            setBeatIds([]);
            setCustomer(null);
          }}
          aria-label="Salesman"
        >
          <option value="">All salesmen</option>
          {salesmen.map((s) => (
            <option key={s.userId} value={s.userId}>
              {s.name}
            </option>
          ))}
        </select>
        <MultiSelect
          options={beatOptions.map((b) => ({ value: b.id, label: b.name }))}
          selected={beatIds}
          onChange={(ids) => {
            setBeatIds(ids);
            setCustomer(null);
          }}
          placeholder={salesmanUserId ? "All beats of salesman" : "All beats"}
        />
        <CustomerPicker
          value={customer}
          onChange={setCustomer}
          beatIds={beatIds}
          salesmanUserId={salesmanUserId || undefined}
          placeholder="All customers (code / name)"
        />
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Submit"}
        </button>
        <button className="secondary" onClick={exportExcel} disabled={exporting || rows.length === 0}>
          {exporting ? "Exporting..." : "Export Excel"}
        </button>
        <button className="secondary" onClick={exportPdf} disabled={exporting || rows.length === 0}>
          Print PDF
        </button>
      </div>

      {statement?.colorCodingEnabled ? <HighlightLegend /> : null}

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>#</th>
              {COLUMNS.map((c) => (
                <th key={c.key} style={{ cursor: "pointer", textAlign: c.align || "left" }} onClick={() => toggle(c.key)}>
                  {c.label}
                  {indicator(c.key)}
                </th>
              ))}
              <th>Remarks</th>
              {statement?.approvalEnabled ? <th>Pending Cheques</th> : null}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <EmptyRow colSpan={COLUMNS.length + 3} title={statement ? "No outstanding invoices" : "Choose filters and press Submit"} />
            ) : (
              sorted.map((row, index) => (
                <tr key={row.invoiceId} style={HIGHLIGHT_STYLES[row.flags?.highlight] || undefined}>
                  <td>{index + 1}</td>
                  {COLUMNS.map((c) => (
                    <td key={c.key} style={{ textAlign: c.align || "left" }} title={String(row[c.key] ?? "")}>
                      {c.format ? c.format(row[c.key]) : row[c.key] ?? "-"}
                    </td>
                  ))}
                  <td title={row.flags?.remarks}>{row.flags?.remarks || "-"}</td>
                  {statement?.approvalEnabled ? <td>{(row.flags?.pendingCheques || []).join(", ") || "-"}</td> : null}
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 ? (
            <tfoot>
              <tr>
                <td colSpan={2} style={{ fontWeight: 700 }}>
                  Total Bills: {statement.totalBills}
                </td>
                <td colSpan={5} />
                <td style={{ fontWeight: 700, textAlign: "right" }}>{money(statement.totalInvoiceAmount)}</td>
                <td style={{ fontWeight: 700, textAlign: "right" }}>{money(statement.totalBalance)}</td>
                <td colSpan={statement.approvalEnabled ? 3 : 2} />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
};

export default OutstandingInvoicesStatement;
